const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL || "").trim();
const SUPABASE_KEY = (process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || "").trim();
const SESSION_KEY = "un_supabase_session_v1";

const jsonHeaders = () => ({
  apikey: SUPABASE_KEY,
  "Content-Type": "application/json",
});

const saveSession = (session) => {
  if (!session?.access_token) return null;
  const normalized = {
    ...session,
    expires_at:
      session.expires_at ||
      (session.expires_in ? Math.floor(Date.now() / 1000) + Number(session.expires_in) : null),
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(normalized));
  return normalized;
};

export const clearSession = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
};

const readStored = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
};

const request = async (path, options = {}) => {
  const res = await fetch(`${SUPABASE_URL}${path}`, options);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body?.msg || body?.message || body?.error_description || body?.error || "Authentication request failed");
    error.status = res.status;
    throw error;
  }
  return body;
};

export const friendlyAuthError = (error, { mode = "signin" } = {}) => {
  const raw = String(error?.message || error || "").trim();
  const msg = raw.toLowerCase();

  if (!msg) return "We couldn’t complete sign-in. Please try again.";

  if (msg.includes("signups not allowed") || msg.includes("user not found") || msg.includes("no user")) {
    return mode === "signin"
      ? "We don’t recognize that email yet. Create an account to continue."
      : "We couldn’t create the account with that email. Please try again.";
  }

  if (msg.includes("email rate limit") || msg.includes("rate limit") || msg.includes("too many requests") || msg.includes("429")) {
    return "Too many sign-in emails were requested. Please wait a little while and try again.";
  }

  if (msg.includes("expired") || msg.includes("invalid token") || msg.includes("otp_expired") || msg.includes("token has expired")) {
    return "That sign-in link has expired or was already used. Request a new one.";
  }

  if (msg.includes("email address not authorized")) {
    return "That email can’t receive a sign-in message yet. Please use another email or try again later.";
  }

  if (msg.includes("invalid email") || msg.includes("unable to validate email")) {
    return "Enter a valid email address.";
  }

  if (msg.includes("session") && (msg.includes("missing") || msg.includes("expired") || msg.includes("invalid"))) {
    return "Your sign-in session has expired. Please sign in again.";
  }

  if (msg.includes("failed to fetch") || msg.includes("network")) {
    return "We couldn’t reach the sign-in service. Check your connection and try again.";
  }

  return "We couldn’t complete sign-in. Please try again.";
};

const isNativeCapacitor = () =>
  typeof window !== "undefined" &&
  window.Capacitor?.isNativePlatform?.();

const authRedirectTo = () => {
  if (isNativeCapacitor()) return "app.unanonymous.client://auth/callback";
  return process.env.REACT_APP_OFFLINE_PREVIEW === "true"
    ? `${window.location.origin}${window.location.pathname}`
    : `${window.location.origin}/auth/callback`;
};

export const sendMagicLink = async ({ email, createUser = true }) => {
  const redirectTo = authRedirectTo();
  await request(`/auth/v1/otp?redirect_to=${encodeURIComponent(redirectTo)}`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ email, create_user: Boolean(createUser) }),
  });
  return true;
};

export const verifyEmailOtp = async ({ email, token }) => {
  const session = await request("/auth/v1/verify", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ email, token, type: "email" }),
  });
  return saveSession(session);
};

export const startOAuth = (provider) => {
  const redirectTo = authRedirectTo();
  window.location.assign(
    `${SUPABASE_URL}/auth/v1/authorize?provider=${encodeURIComponent(provider)}&redirect_to=${encodeURIComponent(redirectTo)}`
  );
};

export const consumeAuthUrl = async (rawUrl) => {
  const parsed = new URL(rawUrl);
  const hash = new URLSearchParams(parsed.hash.replace(/^#/, ""));
  const query = new URLSearchParams(parsed.search);
  const error = hash.get("error_description") || query.get("error_description") || hash.get("error") || query.get("error");
  if (error) throw new Error(error);
  const accessToken = hash.get("access_token") || query.get("access_token");
  if (!accessToken) {
    const existing = await getSession();
    if (existing) return existing;
    throw new Error("The sign-in link did not contain a session. Please request a new link.");
  }
  const session = saveSession({
    access_token: accessToken,
    refresh_token: hash.get("refresh_token") || query.get("refresh_token"),
    token_type: hash.get("token_type") || query.get("token_type") || "bearer",
    expires_in: Number(hash.get("expires_in") || query.get("expires_in") || 3600),
  });
  const user = await getUser(session.access_token);
  return saveSession({ ...session, user });
};

export const consumeAuthCallback = async () => {
  const next = await consumeAuthUrl(window.location.href);
  window.history.replaceState({}, document.title, window.location.pathname);
  return next;
};

export const refreshSession = async (refreshToken) => {
  if (!refreshToken) return null;
  const next = await request("/auth/v1/token?grant_type=refresh_token", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return saveSession(next);
};

export const getUser = async (accessToken) =>
  request("/auth/v1/user", {
    method: "GET",
    headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${accessToken}` },
  });

// A transport outage is not a revoked session. Keep credentials for recovery,
// but return no authenticated session until an expired token refresh succeeds.
const isRetryableAuthError = (error) =>
  error instanceof TypeError || [0, 502, 503, 504].includes(error?.status);

export const getSession = async () => {
  const stored = readStored();
  if (!stored?.access_token) return null;
  const now = Math.floor(Date.now() / 1000);
  let session = stored;
  if (stored.expires_at && stored.expires_at < now + 60) {
    try { session = await refreshSession(stored.refresh_token); }
    catch (error) {
      if (!isRetryableAuthError(error)) clearSession();
      return null;
    }
    if (!session?.access_token) { clearSession(); return null; }
  }
  if (!session?.user) {
    try { session = saveSession({ ...session, user: await getUser(session.access_token) }); }
    catch (error) {
      if (!isRetryableAuthError(error)) clearSession();
      return null;
    }
  }
  return session;
};

export const signOut = async () => {
  const session = readStored();
  try {
    if (session?.access_token) {
      await request("/auth/v1/logout", {
        method: "POST",
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${session.access_token}` },
      });
    }
  } finally { clearSession(); }
};

export const deleteMyAccount = async () => {
  const session = await getSession();
  if (!session?.access_token) throw new Error("Sign in again before deleting your account.");
  const res = await fetch(`${SUPABASE_URL}/functions/v1/delete-my-account`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: "{}",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || "Could not delete account");
  clearSession();
  return true;
};

export const authConfig = {
  url: SUPABASE_URL,
  publishableKey: SUPABASE_KEY,
  googleEnabled: process.env.REACT_APP_ENABLE_GOOGLE_AUTH === "true",
};
